<script lang="ts" setup>
import BaseButton from "@/components/base/BaseButton.vue";
import AsideModal from "@/components/aside/AsideModal.vue";
import NavBar from "@/components/aside/NavBar.vue";
import { useAsideMenu } from "@/composables/aside/useAsideMenu";
import BaseFigureForIcon from "@/components/base/BaseFigureForIcon.vue";
import BaseIcon from "@/components/base/BaseIcon.vue";

const {
  changeMenu,
  clearData,
  currentUserPhone,
  formatPhoneNumber,
  isAsideHidden,
  isAuth,
  isLoadingSelectPhone,
  isLogoHidden,
  isShowList,
  menuList,
  phones,
  configDataBrand,
  setCurrentPhone,
  t,
  isSelectPoint,
} = useAsideMenu();
</script>

<template>
  <aside
    v-show="!isAsideHidden"
    :class="{ hide: isLogoHidden }"
    class="main-header"
  >
    <img
      :src="configDataBrand.assets.logo"
      class="main-header__logo"
      alt="Логотип"
    />
    <img
      class="main-header__logo_mobile"
      :src="configDataBrand.assets.mobileLogo"
      alt="Логотип"
    />
    <div class="main-header--menu" v-if="isAuth">
      <div
        class="main-header--menu-user"
        @click="isShowList = !isShowList"
        :class="{ 'hide-item': isLogoHidden }"
      >
        <BaseFigureForIcon
          icon="account"
          color="secondary"
          size="small"
          type="icon"
        />
        <div>
          <p class="caption-navbar color-text-primary">
            {{ currentUserPhone ? formatPhoneNumber(currentUserPhone) : "" }}
          </p>
        </div>
        <div class="main-header--menu-open color-grey-light">
          <BaseIcon
            name="service-arrow"
            :position="isShowList ? 'top' : 'bottom'"
          />
        </div>
      </div>

      <div v-if="isShowList" class="main-header--menu-list">
        <div
          v-for="item in phones"
          :key="item.phone_number"
          class="main-header--menu-phone-item"
          :class="{ disabled: isLoadingSelectPhone }"
          @click="setCurrentPhone(item.phone_number)"
        >
          <p
            :class="[
              'main-header--menu-phone',
              'caption-navbar',
              { 'color-secondary': item.phone_number === currentUserPhone },
            ]"
          >
            {{ formatPhoneNumber(item.phone_number) }}
          </p>
        </div>
      </div>
      <div class="main-header--menu-list-mobile">
        <AsideModal
          :is-show="isShowList"
          @close="isShowList = false"
          :is-white="true"
          :full-height="false"
        >
          <div class="main-header__modal">
            <p class="main-header__modal-header h1 color-secondary">
              {{ t("aside.selectNumber") }}
            </p>
            <div
              v-for="item in phones"
              :key="item.phone_number"
              :class="[
                'main-header--menu-phone-item',
                {
                  'secondary-background':
                    item.phone_number === currentUserPhone,
                  disabled: isLoadingSelectPhone,
                },
              ]"
              @click="setCurrentPhone(item.phone_number)"
            >
              <p
                :class="[
                  'main-header--menu-phone',
                  'color-text-primary',
                  'caption-navbar',
                  { currentPhone: item.phone_number === currentUserPhone },
                ]"
              >
                {{ formatPhoneNumber(item.phone_number) }}
              </p>
              <BaseFigureForIcon
                :icon="
                  isSelectPoint(item.phone_number) ? 'check-sharp' : 'more'
                "
                size="small"
                :color="
                  isSelectPoint(item.phone_number)
                    ? 'white-opacity-15'
                    : 'secondary'
                "
              />
            </div>
          </div>
        </AsideModal>
      </div>
      <NavBar :menu-items="menuList" @change-menu="changeMenu" />
      <BaseButton
        @click="clearData()"
        :title="t('aside.logout')"
        size="large"
        color="transparent-primary"
        class="main-header--menu-btn"
      />
    </div>
  </aside>
</template>

<style lang="scss" scoped>
@import "@/assets/style/colors";

.main {
  &-header {
    z-index: 4;
    min-width: 260px;
    position: sticky;
    top: 0;
    padding: 44px 0 0 41px;
    @media (max-width: 1100px) {
      padding: 44px 0 0 20px;
    }
    @media (max-width: 960px) {
      display: flex;
      justify-content: center;
      position: static;
      width: 100%;
      flex-direction: column;
    }

    @media (max-width: 960px) {
      display: flex;
      align-items: center;
      padding: 0;
    }

    &__modal {
      padding: 28px 24px;
      gap: 8px;
      display: flex;
      flex-direction: column;

      @media (max-width: 960px) {
        margin-bottom: 100px;
      }

      &-header {
        margin-bottom: 32px;
      }
    }

    &__logo {
      max-width: 71px;
      width: 100%;
      height: auto;

      @media (max-width: 960px) {
        display: none;
      }

      &_mobile {
        display: none;

        @media (max-width: 960px) {
          display: flex;
          position: absolute;
          top: 13px;
          left: 25px;
        }
      }
    }

    &--menu {
      margin-top: 29px;
      display: flex;
      flex-flow: column;
      align-items: flex-start;
      position: relative;

      @media (max-width: 960px) {
        margin-top: 13px;
      }

      &-pic {
      }
      &-user {
        padding: 6px 12px;
        background-color: var(--color-white);
        border-radius: 16px;
        display: flex;
        align-items: center;
        gap: 8px;
        cursor: pointer;
      }

      &-open {
        cursor: pointer;
      }

      &-list {
        background-color: var(--color-white);
        border-radius: 16px;
        display: flex;
        flex-direction: column;
        padding: 24px 64px 24px 16px;
        position: absolute;
        top: 45px;
        box-shadow: 6px 6px 10px 0 rgba(0, 0, 0, 0.3),
          -6px -6px 10px 0 rgba(0, 0, 0, 0.3);
        gap: 20px;
        z-index: 1;

        @media (max-width: 960px) {
          display: none;
        }

        &-mobile {
          display: none;
          @media (max-width: 960px) {
            display: flex;
            position: relative;
          }
        }
      }

      &-phone {
        cursor: pointer;
        transition: color 0.3s ease;
        color: var(--color-grey-light);

        &:hover {
          color: var(--color-secondary);
        }

        &-item {
          @media (max-width: 960px) {
            border-radius: 24px;
            min-height: 72px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            background-color: var(--color-bg-primary);
            padding: 17px 22px 17px 20px;
          }

          &.disabled {
            opacity: 0.5;
            pointer-events: none;
          }
        }
      }

      &-btn {
        display: flex;
        padding: 24px 12px !important;

        @media (max-width: 960px) {
          display: none;
        }

        &:hover {
          border-color: transparent;
        }
      }
    }

    @media (max-width: 650px) {
      top: 35px;
      left: 26px;
    }
  }
}

.hide {
  @media (max-width: 650px) {
    display: none;
  }
}

.hide-item {
  display: none;
}

.main-header--menu-open img {
  transition: transform 0.3s ease;
}

.isOpen {
  transform: rotateX(180deg);
}

.currentPhone {
  color: var(--color-white);
}
.secondary-background {
  background-color: var(--color-secondary);
}
</style>
